// src/components/social/MessageList.jsx
import React, { useContext, useEffect, useRef } from 'react';
import { MessageContext } from '../../context/messageContext';
import MessageItem from './MessageItem';
import Loader from '../common/Loader';
import { FaCommentAlt } from 'react-icons/fa';

const MessageList = ({ conversationId }) => {
  const { messages, loading, loadConversation, markConversationAsRead } = useContext(MessageContext);
  const messagesEndRef = useRef(null);
  const listRef = useRef(null);

  // Group messages by date for date separators
  const groupMessagesByDate = () => {
    const groups = {};
    
    messages.forEach(message => {
      const date = new Date(message.createdAt).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    
    return Object.entries(groups).map(([date, groupMessages]) => {
      return { date, messages: groupMessages };
    });
  };

  // Format date header
  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId);
    }
  }, [conversationId, loadConversation]);

  useEffect(() => {
    // Mark messages as read when viewed
    if (conversationId) {
      markConversationAsRead(conversationId);
    }
  }, [conversationId, messages, markConversationAsRead]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading && messages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  const messageGroups = groupMessagesByDate();

  return (
    <div className="h-full" ref={listRef}>
      {messages.length === 0 ? (
        <div className="h-full flex flex-col items-center justify-center p-6 text-center">
          <div className="bg-gray-100 p-4 rounded-full mb-4">
            <FaCommentAlt className="text-gray-400" size={32} />
          </div>
          <p className="text-gray-500 mb-2">No messages yet.</p>
          <p className="text-gray-400 text-sm">Start the conversation by sending a message below!</p>
        </div>
      ) : (
        <div className="p-4 space-y-6">
          {messageGroups.map(({ date, messages: groupMessages }) => (
            <div key={date}>
              <div className="flex items-center justify-center mb-4">
                <div className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                  {formatDateHeader(date)}
                </div>
              </div>
              
              <div className="space-y-3">
                {groupMessages.map(message => (
                  <MessageItem key={message._id} message={message} />
                ))}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}
    </div>
  );
};

export default MessageList;